import { Button, EButtonColor } from '@web_opxp/components';
import { StatsSelectors, useTranslation } from '@web_opxp/features';
import * as React from 'react';
import { connect } from 'react-redux';
import { GAMEPLAY_FULLSCREEN, SKELETON_CLASSNAME } from '../../base/constants';
import * as FullscreenHelper from '../../base/fullscreenHelper';
import { ChallengeSelectors } from '../../challenge';
import { IState } from '../../store/state';
import { UnitySelectors } from '../../unity';
import ChallengeInfo from '../components/ChallengeInfo';
import MacOsWarning from '../components/MacOsWarning';
import * as Selectors from '../selectors';
import { ExerciseActions } from '../slice';

export interface IIntroViewChallengeProps {
  workoutId: number | null;
  unityLoading: boolean;
  unityProgress: number;
  startDifficulty: number;
  fullscreen: boolean;
  onStartExercise: (difficulty: number) => void;
}

export const IntroViewChallengeFC: React.FC<IIntroViewChallengeProps> = ({
  workoutId,
  unityLoading = false,
  unityProgress,
  startDifficulty,
  fullscreen,
  onStartExercise,
}) => {
  const { t } = useTranslation();
  const isLoading = workoutId === null;
  const skeletonClass = isLoading ? SKELETON_CLASSNAME : '';
  let mainCTALabel = t('Exercise.PreWorkout.Start');
  const mainCTAAction = () => onStartExercise(difficulty);
  if (unityLoading) {
    // calculate percentage
    const progressPercent = Math.round(unityProgress * 100);
    // limit by 90
    const progressValue = Math.min(progressPercent, 90);
    mainCTALabel = t('common:Session.Loading', { progress: progressValue });
  }
  const fullscreenClass = fullscreen ? FullscreenHelper.FULLSCREEN_CLASS : '';
  const mainCTAClass = `${skeletonClass} ${fullscreenClass}`;

  // store difficulty selected by user locally
  const [difficulty, setDifficulty] = React.useState(startDifficulty);
  // store start difficulty propagated from state
  const [savedStartDifficulty, saveStartDifficulty] = React.useState(startDifficulty);
  React.useEffect(() => {
    // new start difficulty propagated from state
    if (startDifficulty !== savedStartDifficulty) {
      setDifficulty(startDifficulty);
      saveStartDifficulty(startDifficulty);
    }
  }, [startDifficulty, savedStartDifficulty]);

  return (
    <>
      <MacOsWarning />
      <ChallengeInfo />
      <div className="exercise-actions">
        <Button
          disabled={unityLoading}
          className={mainCTAClass}
          label={mainCTALabel}
          color={EButtonColor.blue}
          onClick={mainCTAAction}
        />
      </div>
    </>
  );
};

export const mapStateToProps = (state: IState) => {
  const workoutId = ChallengeSelectors.getTodaysChallengeWorkoutId();

  const startDifficulty = Selectors.getWorkoutStartDifficulty(state);

  const unityLoading = !UnitySelectors.isUnityStarted(state);
  const unityProgress = UnitySelectors.getUnityProgress(state);
  const fullscreen = !!StatsSelectors.getStatBooleanValueById(state, GAMEPLAY_FULLSCREEN);
  return {
    workoutId,
    unityLoading,
    unityProgress,
    startDifficulty,
    fullscreen,
  };
};

export const mapDispatchToProps = {
  onStartExercise: ExerciseActions.startExercise,
};

const IntroViewChallenge = connect(mapStateToProps, mapDispatchToProps)(IntroViewChallengeFC);
IntroViewChallenge.displayName = 'IntroViewChallenge';
export default IntroViewChallenge;
